// Content performance scorecard (handoff 7.4): per published piece, a
// read-time join across the systems that already track it —
//   GSC topPages (clicks/impressions/position) matched on url or slug
//   GA4 landing pages (sessions) matched on slug in the path
//   GA4 schedule_call byContent (booked calls) matched on utm_content
//   ActiveCampaign signup sources matched on slug
//   marketing_time_log byContent (hours + soft $) matched on content id
// No schema changes; everything keys on slug / url / utm_campaign.

import { useEffect, useMemo, useState } from 'react';
import { useContent } from '../../../context/ContentContext';
import { useMarketing } from '../MarketingContext';
import { apiFetch } from '../../../utils/apiFetch';
import { Card, Eyebrow } from '../ui';
import { formatCurrency } from '../../../utils/formatting';

// A GSC page url or GA4 path "matches" a piece when it ends with the slug.
function pathMatches(pageUrlOrPath, slug) {
  if (!pageUrlOrPath || !slug) return false;
  const path = pageUrlOrPath.replace(/^https?:\/\/[^/]+/, '').replace(/\/+$/, '');
  return path === `/${slug}` || path.endsWith(`/${slug}`);
}

export default function PerformanceView() {
  const { entries } = useContent();
  const { gsc, traffic, ga4Calls, summary } = useMarketing();
  const [acSources, setAcSources] = useState(null);

  // Signup sources over the last year (the slug is the Signup Source value).
  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/data/ac/sources?days=365')
      .then(r => r.json())
      .then(j => { if (!cancelled && !j.error) setAcSources(j.sources || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => {
    const published = entries.filter(e => e.status === 'published');
    const topPages = gsc.data?.report?.topPages || [];
    const landing = traffic.data?.landingPages || [];
    const callsByContent = ga4Calls.data?.byContent || [];
    const timeByContent = summary.data?.byContent || [];

    return published.map(e => {
      const slug = e.slug || e.utmCampaign;
      const gscRow = topPages.find(p => pathMatches(p.page, slug) || (e.url && p.page === e.url));
      const ga4Row = landing.find(p => pathMatches(p.page, slug));
      const callRow = callsByContent.find(c => c.utmContent === slug || c.utmContent === e.utmCampaign);
      const acRow = (acSources || []).find(s => s.source === slug || s.source === e.utmCampaign);
      const time = timeByContent.find(t => t.contentLogId === e.id);
      return {
        id: e.id,
        title: e.title,
        publishDate: e.publishDate,
        clicks: gscRow?.clicks ?? null,
        impressions: gscRow?.impressions ?? null,
        position: gscRow?.position ?? null,
        sessions: ga4Row?.sessions ?? null,
        signups: acRow?.count ?? null,
        calls: callRow?.count ?? null,
        hours: time?.hours ?? null,
        dollars: time?.dollars ?? null,
      };
    }).sort((a, b) => (b.publishDate || '').localeCompare(a.publishDate || ''));
  }, [entries, gsc.data, traffic.data, ga4Calls.data, summary.data, acSources]);

  const num = (v) => v == null ? <span className="text-ink-3">—</span> : v.toLocaleString();

  return (
    <Card lash="teal" className="p-4">
      <div className="flex items-center justify-between mb-3">
        <Eyebrow>Published · what each piece produced</Eyebrow>
        <span className="text-ink-3 text-[12px]">GSC 28d · GA4 follows the range control · AC 365d</span>
      </div>

      {rows.length === 0 ? (
        <div className="text-[12.5px] text-ink-2">Nothing published yet. Scorecards appear as pieces ship.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {['Piece', 'Clicks', 'Impr.', 'Pos', 'Sessions', 'Signups', 'Calls', 'Invested'].map((h, i) => (
                  <th key={h} className={`text-[10.5px] tracking-[0.06em] uppercase text-ink-3 font-black px-2.5 py-2 border-b border-line ${i === 0 ? 'text-left' : 'text-right'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-[#faf9f6]">
                  <td className="px-2.5 py-2 border-b border-line-2 text-[13px]">
                    <div className="font-semibold text-navy">{r.title}</div>
                    {r.publishDate && <div className="text-[11px] text-ink-3">{r.publishDate}</div>}
                  </td>
                  <Td>{num(r.clicks)}</Td>
                  <Td>{num(r.impressions)}</Td>
                  <Td>{r.position == null ? <span className="text-ink-3">—</span> : r.position.toFixed(1)}</Td>
                  <Td>{num(r.sessions)}</Td>
                  <Td>{num(r.signups)}</Td>
                  <Td strong>{num(r.calls)}</Td>
                  <Td>
                    {r.hours == null ? <span className="text-ink-3">—</span>
                      : <>{r.hours.toFixed(1)}h · {formatCurrency(r.dollars || 0)}</>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[11px] text-ink-3 mt-2.5">
        Joined on slug / url / utm. A dash means that system has no rows for the piece in its window
        (GSC only returns top pages; GA4 landing pages are the top 15 in range).
      </div>
    </Card>
  );
}

function Td({ children, strong }) {
  return (
    <td className={`px-2.5 py-2 border-b border-line-2 text-[13px] text-right tabular-nums ${strong ? 'font-bold text-navy' : 'text-ink-2'}`}>
      {children}
    </td>
  );
}
